/**
 * PricingPage - página de preços com toggle AOA/USD e selector de período.
 * Inclui planos hardcoded como fallback para quando o backend está a acordar (Render Free cold start).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import {
    DEFAULT_BILLING_PERIOD,
    getPlanPricing,
    getPeriod,
    formatTotal,
    formatMonthly,
    formatSavings,
} from "@/lib/billing";
import BillingPeriodToggle from "@/components/shared/BillingPeriodToggle";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

// Planos hardcoded como fallback para quando o backend está a acordar (Render Free)
const FALLBACK_PLANS = [
    {
        id: "economico",
        name: "Económico",
        tagline: "Comece a sua jornada digital",
        price_aoa: 74900,
        price_usd: 87,
        period: "mês",
        features: [
            "1 rede social",
            "10 publicações por mês",
            "Calendário visual",
            "Estatísticas básicas",
            "Suporte por email",
        ],
        max_channels: 1,
        max_posts_per_month: 10,
        has_ai: false,
        has_ads: false,
        multi_user: false,
        highlight: false,
        contact_only: false,
    },
    {
        id: "profissional",
        name: "Profissional",
        tagline: "Para negócios em crescimento",
        price_aoa: 164900,
        price_usd: 192,
        period: "mês",
        features: [
            "3 redes sociais",
            "Publicações ilimitadas",
            "IA para criação de conteúdo",
            "Agendamento automático",
            "Análise avançada",
            "Suporte prioritário",
        ],
        max_channels: 3,
        max_posts_per_month: 999,
        has_ai: true,
        has_ads: true,
        multi_user: false,
        highlight: true,
        contact_only: false,
    },
    {
        id: "empresarial",
        name: "Empresarial",
        tagline: "Gestão completa para empresas",
        price_aoa: 299900,
        price_usd: 349,
        period: "mês",
        features: [
            "6 redes sociais",
            "Publicações ilimitadas",
            "IA avançada + estratégia",
            "Multi-utilizador (5 membros)",
            "Relatórios personalizados",
            "Gestor de conta dedicado",
        ],
        max_channels: 6,
        max_posts_per_month: 999,
        has_ai: true,
        has_ads: true,
        multi_user: true,
        highlight: false,
        contact_only: false,
    },
    {
        id: "agencia",
        name: "Agência",
        tagline: "Solução personalizada para agências",
        price_aoa: 0,
        price_usd: 0,
        period: "mês",
        features: [
            "Clientes ilimitados",
            "White-label disponível",
            "API dedicada",
            "SLA garantido",
            "Formação da equipa",
            "Contrato personalizado",
        ],
        max_channels: 999,
        max_posts_per_month: 999,
        has_ai: true,
        has_ads: true,
        multi_user: true,
        highlight: false,
        contact_only: true,
    },
];

const FAQ = [
    {
        q: "Posso cancelar a qualquer momento?",
        a: "Sim. Não existem fidelizações. Pode cancelar a sua subscrição a qualquer momento. Para subscrições com vários meses, o reembolso é proporcional ao tempo não utilizado.",
    },
    {
        q: "Como faço o pagamento?",
        a: "Aceitamos Multicaixa Express e Transferência Bancária. Faz o pagamento, envia o comprovativo na app e a equipa MeSafa ativa a sua subscrição em poucas horas.",
    },
    {
        q: "Os descontos aplicam-se a todos os planos?",
        a: "Sim, os descontos por período (10% trimestral, 15% semestral, 25% anual) aplicam-se aos planos Económico, Profissional e Empresarial. O plano Agência tem condições personalizadas.",
    },
    {
        q: "A IA escreve mesmo em português de Portugal?",
        a: "Sim. A nossa IA está calibrada para escrever em PT-PT com vocábulario adequado ao mercado angolano e africano.",
    },
    {
        q: "Posso mudar de plano depois?",
        a: "Claro. Pode fazer upgrade ou downgrade a qualquer altura na área de subscrição.",
    },
    {
        q: "E se eu não tiver experiência em redes sociais?",
        a: "Ótimo! A MeSafa foi feita exatamente para si. A nossa IA é o seu estratega digital pessoal.",
    },
];

export default function PricingPage() {
    const [plans, setPlans] = useState(FALLBACK_PLANS);
    const [period, setPeriod] = useState(DEFAULT_BILLING_PERIOD);
    const { currency, setCurrency } = useCurrency();

    useEffect(() => {
        let cancelled = false;
        const fetchPlans = async (attempt = 1) => {
            try {
                const r = await api.get("/public/plans");
                if (!cancelled && r.data && r.data.length > 0) {
                    setPlans(r.data);
                }
            } catch {
                // Retry até 3 vezes com backoff (para cold start do Render Free)
                if (!cancelled && attempt < 3) {
                    setTimeout(() => fetchPlans(attempt + 1), attempt * 5000);
                }
            }
        };
        fetchPlans();
        return () => { cancelled = true; };
    }, []);

    const periodInfo = getPeriod(period);

    return (
        <div data-testid="pricing-page">
            <section className="bg-hero-radial-light">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
                    <Badge
                        variant="outline"
                        className="mb-3 border-[#6B21D9]/30 bg-[#6B21D9]/10 text-[#6B21D9]"
                    >
                        Preços transparentes
                    </Badge>
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
                        Escolha o plano certo para o seu negócio.
                    </h1>
                    <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
                        Sem letras pequenas. Sem fidelização. Cancele quando quiser.
                    </p>

                    <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full bg-card border border-border px-1 py-1">
                            {["AOA", "USD"].map((c) => (
                                <button
                                    key={c}
                                    onClick={() => setCurrency(c)}
                                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                                        currency === c
                                            ? "bg-foreground text-background font-semibold"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                    data-testid={`pricing-currency-${c.toLowerCase()}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>

                        <BillingPeriodToggle
                            value={period}
                            onChange={setPeriod}
                            variant="light"
                            testId="pricing-period-toggle"
                        />
                    </div>

                    {periodInfo.discount_pct > 0 && (
                        <p
                            className="mt-3 text-sm text-[#0F766E] font-medium"
                            data-testid="pricing-period-discount-banner"
                        >
                            ✨ {periodInfo.tag} ao subscrever {periodInfo.short_label.toLowerCase()}.
                        </p>
                    )}
                </div>
            </section>

            <section className="pb-16">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {plans.map((p) => {
                        const isCustom = p.contact_only || p.id === "agencia";
                        const pricing = isCustom ? null : getPlanPricing(p, period);
                        const totalLabel = isCustom ? "Personalizado" : formatTotal(pricing, currency);
                        const monthlyLabel = isCustom ? null : formatMonthly(pricing, currency);
                        const savings = isCustom ? null : formatSavings(pricing, currency);

                        return (
                            <Card
                                key={p.id}
                                className={`p-6 rounded-2xl bg-card relative ${
                                    p.highlight
                                        ? "border-[#6B21D9] shadow-xl ring-1 ring-[#6B21D9]/30"
                                        : "border-border"
                                }`}
                                data-testid={`pricing-plan-${p.id}-card`}
                            >
                                {p.highlight && (
                                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 btn-grad-purple text-white border-none gap-1">
                                        <Sparkles className="h-3 w-3" /> Mais escolhido
                                    </Badge>
                                )}
                                <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
                                <p className="text-sm text-muted-foreground mt-1">{p.tagline}</p>
                                <div className="mt-5 mb-6 min-h-[88px]">
                                    {isCustom ? (
                                        <span className="font-display text-2xl font-bold">Personalizado</span>
                                    ) : (
                                        <>
                                            <div>
                                                <span
                                                    className="font-display text-4xl font-bold"
                                                    data-testid={`pricing-plan-${p.id}-monthly`}
                                                >
                                                    {monthlyLabel}
                                                </span>
                                                <span className="text-muted-foreground text-sm"> /mês</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {periodInfo.months > 1 ? (
                                                    <>
                                                        Total {periodInfo.short_label.toLowerCase()}:{" "}
                                                        <span
                                                            className="font-medium text-foreground"
                                                            data-testid={`pricing-plan-${p.id}-total`}
                                                        >
                                                            {totalLabel}
                                                        </span>
                                                        {savings && (
                                                            <>
                                                                {" · "}
                                                                <span className="text-[#0F766E] font-medium">
                                                                    poupa {savings}
                                                                </span>
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>Faturação mensal</>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <ul className="space-y-3 mb-6">
                                    {p.features.map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm">
                                            <Check className="h-4 w-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    asChild
                                    className={`w-full ${p.highlight ? "btn-grad-purple text-white" : ""}`}
                                    variant={p.highlight ? "default" : "outline"}
                                    data-testid={`pricing-plan-${p.id}-cta`}
                                >
                                    <Link to={isCustom ? "/contacto" : "/registo"}>
                                        {isCustom ? "Falar com vendas" : `Começar com ${p.name}`}
                                    </Link>
                                </Button>
                            </Card>
                        );
                    })}
                </div>
                <p className="mx-auto w-full max-w-6xl px-4 mt-6 text-xs text-center text-muted-foreground">
                    Faturas-recibo emitidas pela MUDS - Prestação de Serviços e Comércio Geral (SU), Lda mediante solicitação. Pode pedir a sua a qualquer momento via geral@mesafa.ao.
                </p>
            </section>

            <section className="py-16 bg-muted/30">
                <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
                    <h2 className="font-display text-3xl font-semibold tracking-tight mb-6">
                        Perguntas frequentes
                    </h2>
                    <Accordion type="single" collapsible className="space-y-2">
                        {FAQ.map((it, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="border border-border rounded-xl bg-card px-4"
                            >
                                <AccordionTrigger className="text-left hover:no-underline">
                                    {it.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-muted-foreground">{it.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
